import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import clsx from 'clsx';

const MenuProductDisplayPrice = ({ product: { display_price }, product, is_product_suggestion = false }) => {
    const { t } = useTranslation('menu.products');

    const size_options = product.getSizeOptions();
    const special_display_prices = ['all', 'from', 'range'];
    const is_not_special_display_price =
        special_display_prices.indexOf(display_price) === -1;
    const is_all_display_price = display_price === 'all';

    const formatPrice = price =>
        t('common:price', {
            price,
        });

    const getDisplayPrice = () => {
        let formatted_lowest_price;
        let formatted_highest_price;
        let formatted_all_prices = [];

        if (!display_price || !size_options) {
            return null; // Exit early if we don't have the required fields
        }

        const sorted_size_options = size_options.sort(
            (a, b) => a.price.toFloat() - b.price.toFloat()
        );

        if (is_all_display_price) {
            // loop over all size options to get the name & price & format accordingly
            const all_prices = sorted_size_options.map(({ name, price }) => {
                const formatted_price = formatPrice(price);

                return (
                    <div key={name} className="m2g-menu-product-price-part">
                        {`${name} ${formatted_price}`}
                    </div>
                );
            });

            formatted_all_prices = all_prices;
        } else {
            // safely check if not `all` as other possibilities are ruled out above
            const lowest_price = sorted_size_options[0]?.price;
            const highest_price =
                sorted_size_options[sorted_size_options.length - 1]?.price;

            formatted_lowest_price = formatPrice(lowest_price);
            formatted_highest_price = formatPrice(highest_price);
        }

        if (
            display_price === 'range' &&
            formatted_lowest_price === formatted_highest_price
        ) {
            return formatted_lowest_price;
        }

        const size_options_to_friendly = {
            from: t('from_price', { lowest: formatted_lowest_price }),
            range: t('range_price', {
                lowest: formatted_lowest_price,
                highest: formatted_highest_price,
            }),
            all: formatted_all_prices,
        };

        return size_options_to_friendly[display_price];
    };

    const renderDisplayPrice = () => {
        if (is_not_special_display_price) {
            return formatPrice(display_price);
        }

        return getDisplayPrice();
    };

    const renderClassType = () => {
        if (!is_product_suggestion){
            // return the standard menu-display class info
            return clsx(
                'm2g-menu-product-price',
                is_not_special_display_price
                    ? 'm2g-menu-product-price--display-price'
                    : 'm2g-menu-product-price--display-price-special',
                is_all_display_price && 'm2g-menu-product-price--multiple-parts'
            );
        } else {
            // return no class info for product suggestions as will be covered elsewhere.
            return '';
        }
    };

    return (
        <span
            className={renderClassType()}
        >
            {renderDisplayPrice()}
        </span>
    );
};

MenuProductDisplayPrice.propTypes = {
    product: PropTypes.shape({
        display_price: PropTypes.string.isRequired,
        getSizeOptions: PropTypes.func.isRequired,
    }).isRequired,
};

export default MenuProductDisplayPrice;
